import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
  Users, MessageSquare, ImageIcon, BookOpen, Trophy,
  Flag, ShieldAlert, Mail, Coins, BarChart3, TrendingUp, TrendingDown,
  ArrowUpRight, MessageSquarePlus, Globe, Smartphone, MailWarning, X, Clock,
} from "lucide-react";

const SECTIONS = ["forum", "galleri", "noveller", "skapere", "konkurranser", "grupper", "annonser", "innstillinger", "dashbord", "annet"];

export default function AdminStatistikk() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [overview, setOverview] = useState(null);
  const [timeseries, setTimeseries] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [trafficSources, setTrafficSources] = useState(null);
  const [topContent, setTopContent] = useState(null);
  const [emailStats, setEmailStats] = useState(null);
  const [reengagementStats, setReengagementStats] = useState(null);
  const [utmData, setUtmData] = useState(null);
  const [utmDays, setUtmDays] = useState(30);
  const [utmSourceFilter, setUtmSourceFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [timeseriesDays, setTimeseriesDays] = useState(30);
  const [trafficDays, setTrafficDays] = useState(7);
  const [hourlyModal, setHourlyModal] = useState(null); // { metric, label }
  const [sourceDays, setSourceDays] = useState(7);

  useEffect(() => {
    if (!authLoading && (!user || !["admin", "owner"].includes(user.role))) {
      nav("/", { replace: true });
    }
  }, [authLoading, user, nav]);

  useEffect(() => {
    if (!user || !["admin", "owner"].includes(user.role)) return;
    setLoading(true);
    Promise.all([
      api.get("/admin/analytics/overview"),
      api.get(`/admin/analytics/timeseries?days=${timeseriesDays}`),
      api.get(`/admin/analytics/traffic?days=${trafficDays}`),
      api.get("/admin/analytics/top-content?days=7&limit=8"),
      api.get("/admin/email-stats").catch(() => ({ data: null })),
      api.get("/admin/reengagement-stats").catch(() => ({ data: null })),
    ]).then(([o, t, tr, tc, es, rs]) => {
      setOverview(o.data);
      setTimeseries(t.data);
      setTraffic(tr.data);
      setTopContent(tc.data);
      setEmailStats(es.data);
      setReengagementStats(rs.data);
    }).finally(() => setLoading(false));
  }, [user, timeseriesDays, trafficDays]);

  useEffect(() => {
    if (!user || !["admin", "owner"].includes(user.role)) return;
    if (tab !== "sources") return;
    api.get(`/admin/analytics/traffic-sources?days=${sourceDays}`)
      .then((r) => setTrafficSources(r.data))
      .catch(() => setTrafficSources(null));
  }, [user, tab, sourceDays]);

  // UTM-fane: lazy-fetch når aktiv eller filter endres
  useEffect(() => {
    if (!user || !["admin", "owner"].includes(user.role)) return;
    if (tab !== "utm") return;
    const q = new URLSearchParams({ days: String(utmDays) });
    if (utmSourceFilter) q.set("source", utmSourceFilter);
    api.get(`/admin/analytics/utm?${q.toString()}`)
      .then((r) => setUtmData(r.data))
      .catch(() => setUtmData(null));
  }, [user, tab, utmDays, utmSourceFilter]);

  if (authLoading || loading || !overview) {
    return <Layout><div className="text-center text-[var(--cream-soft)] py-20">Laster statistikk…</div></Layout>;
  }

  return (
    <Layout withSidebar={false}>
      <div className="max-w-7xl mx-auto px-4 py-8" data-testid="admin-statistikk-page">
        <div className="mb-8">
          <div className="font-meta text-[var(--magenta)] mb-2">ADMIN</div>
          <h1 className="font-fraunces italic text-4xl text-[var(--cream)]">Statistikk og analyse</h1>
        </div>

        {/* Tab-bar */}
        <div className="flex gap-2 mb-8 flex-wrap border-b border-white/10">
          {[
            { k: "overview", label: "Oversikt", icon: BarChart3 },
            { k: "timeseries", label: "Tidsserie", icon: TrendingUp },
            { k: "traffic", label: "Trafikk", icon: ArrowUpRight },
            { k: "sources", label: "Trafikk-kilder", icon: Globe },
            { k: "utm", label: "UTM-kampanjer", icon: ArrowUpRight },
            { k: "top", label: "Topp-innhold", icon: Trophy },
          ].map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.k} onClick={() => setTab(t.k)}
                data-testid={`stat-tab-${t.k}`}
                className={`px-4 py-2.5 font-meta text-xs inline-flex items-center gap-2 border-b-2 -mb-px transition-colors ${
                  tab === t.k
                    ? "border-[var(--magenta)] text-[var(--cream)]"
                    : "border-transparent text-[var(--cream-soft)] hover:text-[var(--cream)]"
                }`}>
                <Icon className="h-4 w-4" /> {t.label.toUpperCase()}
              </button>
            );
          })}
        </div>

        {tab === "overview" && <OverviewTab data={overview} emailStats={emailStats} reengagementStats={reengagementStats} onOpenHourly={setHourlyModal} />}
        {tab === "timeseries" && (
          <TimeseriesTab data={timeseries} days={timeseriesDays} setDays={setTimeseriesDays} />
        )}
        {tab === "traffic" && (
          <TrafficTab data={traffic} days={trafficDays} setDays={setTrafficDays} />
        )}
        {tab === "sources" && (
          <TrafficSourcesTab data={trafficSources} days={sourceDays} setDays={setSourceDays} />
        )}
        {tab === "utm" && (
          <UtmTab
            data={utmData}
            days={utmDays}
            setDays={setUtmDays}
            sourceFilter={utmSourceFilter}
            setSourceFilter={setUtmSourceFilter}
            onOpenHourly={setHourlyModal}
          />
        )}
        {tab === "top" && <TopContentTab data={topContent} />}
      </div>
      {hourlyModal && (
        <HourlyBreakdownModal
          metric={hourlyModal.metric}
          label={hourlyModal.label}
          sublabel={hourlyModal.sublabel}
          onClose={() => setHourlyModal(null)}
        />
      )}
    </Layout>
  );
}

// ============================================================
// OVERSIKT
// ============================================================
function OverviewTab({ data, emailStats, reengagementStats, onOpenHourly }) {
  const setHourlyModal = onOpenHourly || (() => {});
  const { users, content, moderation, economy, features_dau } = data;
  return (
    <div className="space-y-10">
      <Section title="Nye registreringer">
        <StatCard label="Nye siste 24t" value={users.new_24h}
          changePct={users.new_24h_change_pct} icon={Users}
          onClick={() => setHourlyModal({ metric: "registrations", label: "Nye registreringer", sublabel: "Antall pr. time" })} />
        <StatCard label="Nye siste 7 dager" value={users.new_7d}
          changePct={users.new_7d_change_pct} icon={Users} />
        <StatCard label="Nye siste 14 dager" value={users.new_14d}
          changePct={users.new_14d_change_pct} icon={Users} />
        <StatCard label="Nye siste 30 dager" value={users.new_30d}
          changePct={users.new_30d_change_pct} icon={Users} />
      </Section>

      <Section title="Aktivitet">
        <StatCard label="Aktive siste 24t" value={users.active_24h}
          changePct={users.active_24h_change_pct} icon={TrendingUp}
          onClick={() => setHourlyModal({ metric: "active", label: "Aktive brukere", sublabel: "Unike innloggede brukere pr. time" })} />
        <StatCard label="Aktive siste 7 dager" value={users.active_7d}
          changePct={users.active_7d_change_pct} icon={TrendingUp} />
        <StatCard label="Aktive siste 14 dager" value={users.active_14d}
          changePct={users.active_14d_change_pct} icon={TrendingUp} />
        <StatCard label="Aktive siste 30 dager" value={users.active_30d}
          changePct={users.active_30d_change_pct} icon={TrendingUp} />
      </Section>

      <Section title="Konto-status">
        <StatCard label="Totalt registrert" value={users.total} icon={Users} />
        <StatCard label="E-post verifisert" value={users.verified || 0}
          subValue={users.total ? `${Math.round((users.verified / users.total) * 100)}%` : null}
          icon={Users} />
        <Link to="/admin/moderasjon?tab=unverified" className="block" data-testid="link-unverified">
          <StatCard label="Ikke verifisert" value={users.unverified || 0}
            subValue={users.total ? `${Math.round(((users.unverified || 0) / users.total) * 100)}%` : null}
            icon={MailWarning} accent={(users.unverified || 0) > 0 ? "warn" : null} />
        </Link>
        <StatCard label="Suspenderte" value={users.suspended} icon={ShieldAlert}
          accent={users.suspended > 0 ? "warn" : null} />
      </Section>

      {features_dau && (
        <Section title="Aktive per feature (siste 24t)">
          <StatCard
            label="Utforsk"
            value={features_dau.utforsk || 0}
            subValue="unike brukere"
            icon={TrendingUp}
            accent
          />
          <StatCard
            label="Glimt"
            value={features_dau.glimt || 0}
            subValue="unike seere"
            icon={TrendingUp}
            accent
          />
          <StatCard
            label="Gruppechat"
            value={features_dau.group_chat || 0}
            subValue="aktive sendere"
            icon={TrendingUp}
            accent
          />
        </Section>
      )}

      <Section title="Roller">
        <StatCard label="Skapere" value={users.creators} icon={Users} accent
          subValue={users.creators_active != null ? `${users.creators_active} synlige nå` : null} />
        <StatCard label="Verifiserte selgere" value={users.sellers} icon={Users} accent />
        <StatCard label="Aktive VIP" value={users.vip_active} icon={Users} accent="gold" />
      </Section>

      <Section title="Innhold">
        <StatCard label="Tråder" value={content.threads_total} subValue={`+${content.threads_7d} siste 7d`} icon={MessageSquare} />
        <StatCard label="Innlegg" value={content.posts_total} subValue={`+${content.posts_7d} siste 7d`} icon={MessageSquare} />
        <StatCard label="Bilder" value={content.images_total} subValue={`+${content.images_7d} siste 7d`} icon={ImageIcon} />
        <StatCard label="Noveller" value={content.noveller_total} subValue={`+${content.noveller_7d} siste 7d`} icon={BookOpen} />
        <StatCard label="Album" value={content.albums_total} icon={ImageIcon} />
        <StatCard label="Konkurranser" value={content.contests_total} subValue={`${content.contests_active} aktive`} icon={Trophy} />
      </Section>

      <Section title="Modereringskøer">
        <StatCard label="Åpne rapporter" value={moderation.open_reports}
          accent={moderation.open_reports > 0 ? "warn" : null} icon={Flag} />
        <StatCard label="Rapporter siste 7d" value={moderation.reports_7d} icon={Flag} />
        <StatCard label="CSAM venter" value={moderation.csam_pending}
          accent={moderation.csam_pending > 0 ? "warn" : null} icon={ShieldAlert} />
        <StatCard label="Skaper-søknader" value={moderation.creator_apps_pending + moderation.creator_apps_code_sent}
          subValue={`${moderation.creator_apps_pending} nye / ${moderation.creator_apps_code_sent} venter på kode`}
          accent={moderation.creator_apps_pending > 0 ? "magenta" : null} icon={Users} />
        <StatCard label="Selger-verifisering" value={moderation.seller_verif_pending}
          accent={moderation.seller_verif_pending > 0 ? "magenta" : null} icon={Users} />
        <StatCard label="Coaching-forespørsler" value={moderation.coaching_new}
          subValue={`${moderation.coaching_total} totalt`}
          accent={moderation.coaching_new > 0 ? "magenta" : null} icon={Mail} />
        <StatCard label="Tilbakemeldinger" value={moderation.feedback_new || 0}
          subValue={`${moderation.feedback_in_progress || 0} pågår · ${moderation.feedback_total || 0} totalt · ${moderation.feedback_7d || 0} siste 7d`}
          accent={(moderation.feedback_new || 0) > 0 ? "magenta" : null} icon={MessageSquarePlus} />
      </Section>

      <Section title="Økonomi (chips)">
        <StatCard label="Chips i omløp" value={fmt(economy.chips_in_circulation)} icon={Coins} accent="gold" />
        <StatCard label="Kjøp totalt" value={economy.purchases_total_count}
          subValue={`${fmt(economy.purchases_total_chips)} chips · ${fmt(economy.purchases_total_nok)} NOK`} icon={Coins} />
        <StatCard label="Kjøp siste 7d" value={economy.purchases_7d_count}
          subValue={`${fmt(economy.purchases_7d_chips)} chips · ${fmt(economy.purchases_7d_nok)} NOK`} icon={Coins} accent="gold" />
      </Section>

      {emailStats && <EmailStatsSection stats={emailStats} />}
      {reengagementStats && <ReengagementStatsSection stats={reengagementStats} />}
    </div>
  );
}

// ============================================================
// E-POST-STATISTIKK
// ============================================================
const KIND_LABELS = {
  email_verification: "Verifisering",
  welcome: "Velkomst",
  password_reset: "Glemt passord",
  email_change_code: "Bytte av e-post",
  creator_trial_ending: "Skaper-prøveperiode utløper",
  wheel_winner: "Lykkehjul-vinst",
  reengagement_14d: "Re-engasjement 14d",
  reengagement_90d: "Re-engasjement 90d",
  coaching_reply: "Coaching-svar",
  other: "Annet",
};

function ReengagementStatsSection({ stats }) {
  const { d14, d90 } = stats || {};
  const [recipients, setRecipients] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const loadRecipients = async () => {
    setOpen(!open);
    if (recipients) return;
    try {
      const r = await api.get("/admin/reengagement-recipients?kind=d14");
      setRecipients(r.data?.recipients || []);
    } catch {
      setRecipients([]);
    }
  };
  if (!d14 && !d90) return null;
  return (
    <section className="mt-8" data-testid="reengagement-stats-section">
      <h2 className="font-meta text-xs text-[var(--cream-soft)] tracking-[0.2em] mb-4">RE-ENGASJEMENT-MAIL</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <StatCard label="14d: Sendt totalt" value={d14?.total_sent ?? 0} icon={Mail} />
        <StatCard label="14d: Reaktivert" value={d14?.reactivated ?? 0} icon={Mail} accent="gold" />
        <StatCard label="14d: Reaktiveringsrate" value={`${d14?.reactivation_rate ?? 0}%`} icon={Mail} />
        <StatCard label="14d: Chips aktivert" value={`${d14?.chips_claimed ?? 0} / ${d14?.total_sent ?? 0}`} icon={Mail} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
        <StatCard label="14d: Reservert chips" value={d14?.chips_total_reserved ?? 0} icon={Mail} />
        <StatCard label="14d: Utbetalt chips" value={d14?.chips_total_paid_out ?? 0} icon={Mail} accent="gold" />
        <StatCard label="14d: Pending (ikke innlogget)" value={d14?.chips_pending ?? 0} icon={Mail} accent={d14?.chips_pending > 0 ? "warn" : null} />
      </div>
      {d90 && d90.total_sent > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
          <StatCard label="90d: Sendt totalt" value={d90.total_sent} icon={Mail} />
          <StatCard label="90d: Reaktivert" value={d90.reactivated} icon={Mail} accent="gold" />
          <StatCard label="90d: Reaktiveringsrate" value={`${d90.reactivation_rate}%`} icon={Mail} />
        </div>
      )}
      <button onClick={loadRecipients} className="btn-ghost text-xs" data-testid="show-reengagement-recipients">
        {open ? "Skjul mottakerliste" : "Vis hvem som fikk mailen (14d)"}
      </button>
      {open && recipients && (
        <div className="panel-tight overflow-x-auto mt-3">
          <table className="w-full text-sm" data-testid="reengagement-recipients-table">
            <thead>
              <tr className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] border-b border-white/10">
                <th className="text-left py-2 px-3">BRUKER</th>
                <th className="text-left py-2 px-3">E-POST</th>
                <th className="text-left py-2 px-3">SENDT</th>
                <th className="text-left py-2 px-3">SIST INNE</th>
                <th className="text-right py-2 px-3">STATUS</th>
              </tr>
            </thead>
            <tbody>
              {recipients.map((r) => (
                <tr key={r.user_id} className="border-b border-white/5">
                  <td className="py-2 px-3 text-[var(--cream)]">{r.username || "-"}</td>
                  <td className="py-2 px-3 text-[var(--cream-soft)]">{r.email || "-"}</td>
                  <td className="py-2 px-3 text-[var(--cream-soft)]">{r.sent_at ? new Date(r.sent_at).toLocaleString("nb-NO") : "-"}</td>
                  <td className="py-2 px-3 text-[var(--cream-soft)]">{r.last_seen ? new Date(r.last_seen).toLocaleString("nb-NO") : "-"}</td>
                  <td className="py-2 px-3 text-right font-meta text-xs">
                    {r.reactivated ? <span className="text-[var(--gold)]">REAKTIVERT</span> : <span className="text-[var(--cream-faint)]">VENTER</span>}
                  </td>
                </tr>
              ))}
              {recipients.length === 0 && (
                <tr><td colSpan={5} className="py-4 text-center text-[var(--cream-faint)]">Ingen mottakere ennå.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}


function EmailStatsSection({ stats }) {
  const { totals, by_kind } = stats;
  return (
    <section>
      <h2 className="font-meta text-xs text-[var(--cream-soft)] tracking-[0.2em] mb-4">E-POST</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <StatCard label="Sendt siste 24t" value={totals.h24} icon={Mail} />
        <StatCard label="Sendt siste 7d" value={totals.d7} icon={Mail} />
        <StatCard label="Sendt siste 30d" value={totals.d30} icon={Mail} />
        <StatCard label="Sendt totalt" value={totals.all} icon={Mail} accent="gold" />
        <StatCard label="Feilet 24t" value={totals.failed_24h}
          icon={ShieldAlert} accent={totals.failed_24h > 0 ? "warn" : null} />
      </div>
      {by_kind && by_kind.length > 0 && (
        <div className="panel-tight overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] border-b border-white/10">
                <th className="text-left py-2 px-3">TYPE</th>
                <th className="text-right py-2 px-3">24T</th>
                <th className="text-right py-2 px-3">7D</th>
                <th className="text-right py-2 px-3">30D</th>
                <th className="text-right py-2 px-3">TOTALT</th>
              </tr>
            </thead>
            <tbody>
              {by_kind.map((r) => (
                <tr key={r.kind} className="border-b border-white/5">
                  <td className="py-2 px-3 text-[var(--cream)]">{KIND_LABELS[r.kind] || r.kind}</td>
                  <td className="py-2 px-3 text-right text-[var(--cream-soft)]">{r.h24}</td>
                  <td className="py-2 px-3 text-right text-[var(--cream-soft)]">{r.d7}</td>
                  <td className="py-2 px-3 text-right text-[var(--cream-soft)]">{r.d30}</td>
                  <td className="py-2 px-3 text-right text-[var(--cream)] font-meta">{r.all}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ============================================================
// TIDSSERIE
// ============================================================
function TimeseriesTab({ data, days, setDays }) {
  if (!data) return null;
  const metrics = [
    { key: "new_users", label: "Nye brukere", color: "#D11A5B" },
    { key: "new_threads", label: "Nye tråder", color: "#5BA5C9" },
    { key: "new_posts", label: "Nye innlegg", color: "#82B488" },
    { key: "new_images", label: "Nye bilder", color: "#E8B563" },
    { key: "new_noveller", label: "Nye noveller", color: "#C58CD9" },
    { key: "page_views", label: "Sidetreff", color: "#F2EBE0" },
  ];
  return (
    <div>
      <div className="flex gap-2 mb-6 flex-wrap">
        {[7, 14, 30, 60, 90].map((d) => (
          <button key={d} onClick={() => setDays(d)}
            className={`px-3 py-1.5 font-meta text-xs ${
              days === d ? "bg-[var(--magenta)] text-black" : "border border-white/15 text-[var(--cream-soft)] hover:border-[var(--cream)]"
            }`}>
            Siste {d}d
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {metrics.map((m) => (
          <BarChart key={m.key} title={m.label} color={m.color} series={data[m.key] || []} />
        ))}
      </div>
    </div>
  );
}

function BarChart({ title, color, series }) {
  const total = series.reduce((s, p) => s + p.count, 0);
  const max = Math.max(...series.map((p) => p.count), 1);
  // Hvor ofte vi skal vise dato-etiketter på x-aksen
  const labelEvery = series.length <= 14 ? 1 : series.length <= 30 ? 3 : series.length <= 60 ? 5 : 7;
  // Minimums-høyde for tall-etiketten over baren: kun vis hvis baren er > 12%
  // for å unngå overlap med småe naboer.
  const showLabelThreshold = 0.12;
  return (
    <div className="panel-tight p-5">
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="font-fraunces italic text-xl text-[var(--cream)]">{title}</h3>
        <span className="font-meta text-xs text-[var(--cream-soft)]">SUM: <strong className="text-[var(--cream)]">{fmt(total)}</strong></span>
      </div>
      {series.length === 0 ? (
        <div className="text-center py-10 text-[var(--cream-faint)] text-sm">Ingen data for perioden.</div>
      ) : (
        <>
          <div className="flex items-end gap-0.5 h-36 border-b border-l border-white/10 pl-1 pb-1 pt-5">
            {series.map((p) => {
              const h = (p.count / max) * 100;
              const showInlineLabel = p.count > 0 && (p.count / max) >= showLabelThreshold;
              return (
                <div key={p.date} className="flex-1 min-w-[6px] relative group flex items-end justify-center">
                  {/* Tall-etikett over baren (synlig hvis baren er stor nok) */}
                  {showInlineLabel && (
                    <span
                      className="absolute -top-4 left-1/2 -translate-x-1/2 text-[10px] font-meta text-[var(--cream)] pointer-events-none whitespace-nowrap"
                    >
                      {p.count}
                    </span>
                  )}
                  <div style={{
                    height: `${h}%`,
                    background: color,
                    opacity: 0.7,
                    width: "100%",
                    minHeight: p.count > 0 ? 4 : 0,
                    transition: "opacity 0.15s",
                  }} className="hover:opacity-100" />
                  {/* Tooltip ved hover */}
                  <div className="absolute -top-9 left-1/2 -translate-x-1/2 bg-black/95 border border-white/20 px-2 py-1 text-[10px] text-[var(--cream)] opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-10">
                    {p.date}: <strong>{p.count}</strong>
                  </div>
                </div>
              );
            })}
          </div>
          {/* X-akse: datoer */}
          <div className="flex gap-0.5 pl-1 mt-1.5">
            {series.map((p, i) => (
              <div key={p.date} className="flex-1 min-w-[6px] text-center">
                {i % labelEvery === 0 && (
                  <span className="font-meta text-[9px] text-[var(--cream-faint)]">
                    {p.date.slice(5).replace("-", "/")}
                  </span>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================
// TRAFIKK
// ============================================================
function TrafficTab({ data, days, setDays }) {
  if (!data) return null;
  const max = Math.max(...data.by_section.map((s) => s.views), 1);
  return (
    <div>
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-4">
        <div className="flex gap-2">
          {[1, 7, 14, 30].map((d) => (
            <button key={d} onClick={() => setDays(d)}
              className={`px-3 py-1.5 font-meta text-xs ${
                days === d ? "bg-[var(--magenta)] text-black" : "border border-white/15 text-[var(--cream-soft)] hover:border-[var(--cream)]"
              }`}>
              Siste {d}d
            </button>
          ))}
        </div>
        <div className="font-meta text-xs text-[var(--cream-soft)] flex gap-6">
          <span>TOTALT: <strong className="text-[var(--cream)]">{fmt(data.total_views)}</strong> sidetreff</span>
          <span>UNIKE: <strong className="text-[var(--cream)]">{fmt(data.unique_users)}</strong> brukere</span>
        </div>
      </div>

      <div className="panel-tight p-6">
        {data.by_section.length === 0 ? (
          <div className="text-center py-12 text-[var(--cream-soft)]">
            <p className="font-fraunces italic text-xl mb-2">Ingen trafikkdata enda.</p>
            <p className="text-sm">Sidetreff begynner å logges når brukere navigerer. Kom tilbake om noen timer.</p>
          </div>
        ) : (
          <ul className="space-y-3">
            {data.by_section.map((s) => (
              <li key={s.section} className="grid grid-cols-12 items-center gap-3" data-testid={`traffic-row-${s.section}`}>
                <div className="col-span-3 font-meta text-sm text-[var(--cream)] uppercase">{s.section}</div>
                <div className="col-span-7 h-3 bg-white/5 relative">
                  <div style={{
                    width: `${(s.views / max) * 100}%`,
                    background: "linear-gradient(90deg, var(--magenta), rgba(209,26,91,0.4))",
                    height: "100%",
                  }} />
                </div>
                <div className="col-span-2 text-right font-meta text-sm text-[var(--cream)]">
                  {fmt(s.views)} <span className="text-[var(--cream-faint)] text-xs">({s.unique_users}u)</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ============================================================
// TOPP-INNHOLD
// ============================================================
// ============================================================
// UTM-KAMPANJER – detaljert sporing av lenker med utm-tagger
// ============================================================
function UtmTab({ data, days, setDays, sourceFilter, setSourceFilter, onOpenHourly }) {
  const setHourlyModal = onOpenHourly || (() => {});
  if (!data) {
    return (
      <div className="space-y-6">
        <UtmDaysSelector days={days} setDays={setDays} />
        <div className="panel-tight p-12 text-center text-[var(--cream-soft)]">Laster…</div>
      </div>
    );
  }

  const { total_views, unique_visitors, sources, campaigns, mediums, combos, daily } = data;

  return (
    <div className="space-y-6" data-testid="utm-tab">
      {/* Tools-rad */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <UtmDaysSelector days={days} setDays={setDays} />
        {sourceFilter && (
          <button
            onClick={() => setSourceFilter(null)}
            className="px-3 py-1.5 font-meta text-xs border border-[var(--magenta)] text-[var(--magenta)] hover:bg-[var(--magenta)] hover:text-black inline-flex items-center gap-2"
            data-testid="utm-clear-filter"
          >
            FILTER: {sourceFilter.toUpperCase()} ✕
          </button>
        )}
      </div>

      {/* Top-line stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard label={sourceFilter ? `Treff på «${sourceFilter}»` : "UTM-treff totalt"}
          value={fmt(total_views)} icon={ArrowUpRight} accent="gold"
          onClick={() => setHourlyModal({ metric: "utm_hits", label: "UTM-treff totalt", sublabel: "Treff totalt pr. time (inkluderer gjentatte besøk)" })} />
        <StatCard label="Unike besøkende" value={fmt(unique_visitors)} icon={Users}
          onClick={() => setHourlyModal({ metric: "utm_visitors", label: "Unike UTM-besøkende", sublabel: "Unike besøkende pr. time" })} />
        <StatCard label="Registreringer" value={fmt(data.total_signups || 0)}
          subValue={data.conversion_pct ? `${data.conversion_pct}% konvertering` : null}
          icon={Users} accent />
        <StatCard label="Kilder" value={sources.length} icon={Globe} />
        <StatCard label="Kampanjer" value={campaigns.length} icon={MessageSquare} />
      </div>

      {/* Hjelpetekst */}
      <div className="panel-tight p-4 text-xs text-[var(--cream-soft)]">
        <strong className="text-[var(--cream)]">Slik bruker du UTM-tagger:</strong> Legg til
        <code className="px-1 mx-1 bg-black/40 text-[var(--magenta)] font-mono">
          ?utm_source=snapchat&amp;utm_campaign=ad_juni&amp;utm_medium=story
        </code>
        på slutten av lenkene du deler. Da kan du se eksakt hvor mye trafikk hver kilde gir – også
        Snapchat/Instagram/TikTok som ikke sender referrer-header. Klikk en kilde i listen for å filtrere på den.
      </div>

      {/* Daglig trend */}
      {sourceFilter && (
        <BarChart
          title={`Daglig trafikk fra «${sourceFilter}»`}
          color="#F71868"
          series={daily.map((d) => ({ date: d.date, count: d.views }))}
        />
      )}

      {/* 3 lister side ved side */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <UtmList
          title="Kilder (source)"
          items={sources}
          onClick={(key) => setSourceFilter(key)}
          highlight={sourceFilter}
          emptyText="Ingen UTM-trafikk enda."
        />
        <UtmList title="Kampanjer (campaign)" items={campaigns} emptyText="Ingen kampanje-tagger satt." />
        <UtmList title="Medium" items={mediums} emptyText="Ingen medium-tagger satt." />
      </div>

      {/* Kombinasjoner-tabell */}
      {combos.length > 0 && (
        <div>
          <h3 className="font-fraunces italic text-xl text-[var(--cream)] mb-3">Topp kombinasjoner</h3>
          <div className="panel-tight overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] border-b border-white/10">
                  <th className="text-left py-2 px-3">KILDE</th>
                  <th className="text-left py-2 px-3">KAMPANJE</th>
                  <th className="text-left py-2 px-3">MEDIUM</th>
                  <th className="text-right py-2 px-3">TREFF</th>
                  <th className="text-right py-2 px-3">UNIKE</th>
                  <th className="text-right py-2 px-3">REGISTRERT</th>
                  <th className="text-right py-2 px-3">KONV %</th>
                </tr>
              </thead>
              <tbody>
                {combos.map((c, i) => (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/5" data-testid={`utm-combo-${i}`}>
                    <td className="py-2 px-3 text-[var(--cream)]">{c.source || "—"}</td>
                    <td className="py-2 px-3 text-[var(--cream-soft)]">{c.campaign || "—"}</td>
                    <td className="py-2 px-3 text-[var(--cream-soft)]">{c.medium || "—"}</td>
                    <td className="py-2 px-3 text-right text-[var(--cream)] font-meta">{fmt(c.views)}</td>
                    <td className="py-2 px-3 text-right text-[var(--cream-soft)]">{fmt(c.unique)}</td>
                    <td className="py-2 px-3 text-right text-[var(--magenta)] font-meta">{fmt(c.signups || 0)}</td>
                    <td className="py-2 px-3 text-right text-[var(--cream-soft)] font-meta">
                      {c.conversion_pct ? `${c.conversion_pct}%` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function UtmDaysSelector({ days, setDays }) {
  return (
    <div className="flex gap-1">
      {[1, 7, 30, 90].map((d) => (
        <button
          key={d}
          onClick={() => setDays(d)}
          data-testid={`utm-days-${d}`}
          className={`px-3 py-1.5 font-meta text-xs border ${
            days === d
              ? "bg-[var(--magenta)] text-black border-[var(--magenta)]"
              : "border-white/20 text-[var(--cream-soft)] hover:border-white/40"
          }`}
        >
          {d}D
        </button>
      ))}
    </div>
  );
}

function UtmList({ title, items, onClick, highlight, emptyText }) {
  return (
    <div className="panel-tight p-4">
      <h3 className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] mb-3">{title.toUpperCase()}</h3>
      {items.length === 0 ? (
        <div className="text-xs text-[var(--cream-faint)] italic">{emptyText}</div>
      ) : (
        <ul className="space-y-1.5">
          {items.map((it, i) => {
            const isActive = highlight && highlight === it.key;
            const inner = (
              <>
                <span className={`text-sm flex-1 truncate ${isActive ? "text-[var(--magenta)]" : "text-[var(--cream)]"}`}>
                  {it.key || "—"}
                </span>
                <span className="font-meta text-xs text-[var(--cream)] tabular-nums ml-2">{fmt(it.views)}</span>
                <span className="font-meta text-xs text-[var(--cream-faint)] tabular-nums ml-2 hidden sm:inline">
                  ({fmt(it.unique)})
                </span>
                {it.signups > 0 && (
                  <span className="font-meta text-xs text-[var(--magenta)] tabular-nums ml-2" title={`${it.signups} registrert (${it.conversion_pct}% konv.)`}>
                    →{fmt(it.signups)}
                  </span>
                )}
              </>
            );
            const cls = "flex items-center justify-between px-2 py-1.5 -mx-2 transition-colors";
            return (
              <li key={i}>
                {onClick ? (
                  <button
                    onClick={() => onClick(it.key)}
                    className={`${cls} w-full text-left hover:bg-white/5 ${isActive ? "bg-[var(--magenta)]/10" : ""}`}
                    data-testid={`utm-item-${i}`}
                  >
                    {inner}
                  </button>
                ) : (
                  <div className={cls} data-testid={`utm-item-${i}`}>{inner}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}


function TrafficSourcesTab({ data, days, setDays }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-4">
        <div className="flex gap-2">
          {[1, 7, 14, 30].map((d) => (
            <button key={d} onClick={() => setDays(d)}
              data-testid={`sources-range-${d}`}
              className={`px-3 py-1.5 font-meta text-xs ${
                days === d ? "bg-[var(--magenta)] text-black" : "border border-white/15 text-[var(--cream-soft)] hover:border-[var(--cream)]"
              }`}>
              Siste {d}d
            </button>
          ))}
        </div>
        {data && (
          <div className="font-meta text-xs text-[var(--cream-soft)] flex gap-6">
            <span>TOTALT: <strong className="text-[var(--cream)]">{fmt(data.total_views)}</strong> sidetreff</span>
            <span>UNIKE: <strong className="text-[var(--cream)]">{fmt(data.unique_visitors)}</strong> besøkende</span>
          </div>
        )}
      </div>

      {!data ? (
        <div className="text-center py-12 text-[var(--cream-soft)]">Laster…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SourcePanel
            title="Henvisende kilder"
            subtitle="Hvor brukere kommer fra (Google, Reddit, sosiale medier, direkte)"
            items={data.referrers}
            keyField="key"
            labelField="label"
            testid="sources-referrers"
          />
          <SourcePanel
            title="Land"
            subtitle="Geografisk fordeling (basert på IP)"
            items={data.countries}
            keyField="key"
            labelField="label"
            keyFallback="?"
            testid="sources-countries"
          />
          <SourcePanel
            title="Topp byer"
            items={data.cities}
            keyField="key"
            testid="sources-cities"
          />
          <SourcePanel
            title="Enhet"
            items={data.devices}
            keyField="key"
            iconMap={{
              mobile: Smartphone,
              tablet: Smartphone,
              desktop: BarChart3,
            }}
            labels={{
              mobile: "Mobil",
              tablet: "Tablet",
              desktop: "Desktop",
              unknown: "Ukjent",
            }}
            testid="sources-devices"
          />
          <SourcePanel
            title="Topp landingssider"
            subtitle="Første side brukere åpner per session"
            items={data.landing_pages}
            keyField="key"
            testid="sources-landing"
          />
          <SourcePanel
            title="UTM-kampanjer"
            subtitle="Bruk f.eks. ?utm_source=reddit&utm_campaign=lansering i annonse-lenker"
            items={data.utm_campaigns}
            keyField="key"
            empty="Ingen UTM-data ennå – tag annonse-lenker med ?utm_campaign=navn"
            testid="sources-utm"
          />
        </div>
      )}
    </div>
  );
}

function SourcePanel({ title, subtitle, items, keyField, labelField, keyFallback, iconMap, labels, empty, testid }) {
  const list = Array.isArray(items) ? items : [];
  const max = Math.max(...list.map((r) => r.views), 1);
  return (
    <div className="panel-tight p-5" data-testid={testid}>
      <div className="mb-4">
        <h3 className="font-fraunces italic text-lg text-[var(--cream)]">{title}</h3>
        {subtitle && <p className="text-xs text-[var(--cream-faint)] mt-1">{subtitle}</p>}
      </div>
      {list.length === 0 ? (
        <div className="text-xs text-[var(--cream-faint)] italic py-3">
          {empty || "Ingen data ennå."}
        </div>
      ) : (
        <ul className="space-y-2.5">
          {list.map((r, i) => {
            const rawKey = r[keyField];
            const label = (labels && labels[rawKey]) || r[labelField] || rawKey || keyFallback || "—";
            const Icon = iconMap && iconMap[rawKey];
            return (
              <li key={`${rawKey}-${i}`} className="grid grid-cols-12 items-center gap-2">
                <div className="col-span-5 font-meta text-xs text-[var(--cream)] truncate flex items-center gap-1.5">
                  {Icon && <Icon className="h-3 w-3 shrink-0" />}
                  <span className="truncate" title={label}>{label}</span>
                </div>
                <div className="col-span-5 h-2 bg-white/5 relative">
                  <div style={{
                    width: `${(r.views / max) * 100}%`,
                    background: "linear-gradient(90deg, var(--magenta), rgba(209,26,91,0.4))",
                    height: "100%",
                  }} />
                </div>
                <div className="col-span-2 text-right font-meta text-xs text-[var(--cream)]">
                  {fmt(r.views)}
                  {typeof r.unique === "number" && (
                    <span className="text-[var(--cream-faint)] ml-1">({r.unique})</span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function TopContentTab({ data }) {
  if (!data) return null;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <TopList title="Mest sette tråder (7d)"
        rows={data.top_threads}
        rowKey={(t) => t.id}
        render={(t) => (
          <>
            <div className="font-fraunces italic text-[var(--cream)] truncate">{t.title}</div>
            <div className="font-meta text-[10px] text-[var(--cream-faint)] mt-0.5">
              {t.view_count}{" "}sidetreff · {t.post_count} innlegg · av {t.author?.username || "?"}
            </div>
          </>
        )} />
      <TopList title="Mest likte bilder (7d)"
        rows={data.top_images}
        rowKey={(i) => i.id}
        render={(i) => (
          <>
            <div className="font-fraunces italic text-[var(--cream)] truncate">{i.caption || "(uten tittel)"}</div>
            <div className="font-meta text-[10px] text-[var(--cream-faint)] mt-0.5">
              ❤ {i.like_count} · 👁 {i.view_count} · av {i.uploader_username || "?"}
            </div>
          </>
        )} />
      <TopList title="Mest leste noveller (7d)"
        rows={data.top_noveller}
        rowKey={(n) => n.id}
        render={(n) => (
          <>
            <div className="font-fraunces italic text-[var(--cream)] truncate">{n.title}</div>
            <div className="font-meta text-[10px] text-[var(--cream-faint)] mt-0.5">
              📖 {n.read_count} · ❤ {n.like_count} · av {n.author_username || "?"}
            </div>
          </>
        )} />
      <TopList title="Mest aktive brukere (7d)"
        rows={data.top_users}
        rowKey={(u) => u.id}
        render={(u) => (
          <>
            <div className="font-fraunces italic text-[var(--cream)]">@{u.username}</div>
            <div className="font-meta text-[10px] text-[var(--cream-faint)] mt-0.5">
              {u.actions} handlinger
            </div>
          </>
        )} />
    </div>
  );
}

function TopList({ title, rows, rowKey, render }) {
  return (
    <div className="panel-tight p-5">
      <h3 className="font-fraunces italic text-xl text-[var(--cream)] mb-4">{title}</h3>
      {(!rows || rows.length === 0) ? (
        <div className="py-8 text-center text-[var(--cream-faint)] text-sm">Ingen data ennå.</div>
      ) : (
        <ol className="space-y-3">
          {rows.map((r, idx) => (
            <li key={rowKey(r)} className="grid grid-cols-12 gap-3 items-start">
              <span className="col-span-1 font-meta text-[var(--cream-faint)] text-sm pt-1">{idx + 1}.</span>
              <div className="col-span-11 min-w-0">{render(r)}</div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// ============================================================
// Helpers
// ============================================================
function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-meta text-xs text-[var(--cream-soft)] tracking-[0.2em] mb-4">{title.toUpperCase()}</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">{children}</div>
    </section>
  );
}

function StatCard({ label, value, subValue, changePct, icon: Icon, accent, onClick }) {
  const accentColor = accent === "magenta" ? "var(--magenta)"
    : accent === "gold" ? "var(--gold)"
    : accent === "warn" ? "#E26C6C"
    : accent === true ? "var(--magenta)"
    : "var(--cream-soft)";
  const clickable = !!onClick;
  const Tag = clickable ? "button" : "div";
  return (
    <Tag
      type={clickable ? "button" : undefined}
      onClick={onClick}
      data-testid={clickable ? `statcard-clickable-${label.toLowerCase().replace(/\s+/g, "-")}` : undefined}
      className={`panel-tight p-5 text-left w-full ${clickable ? "cursor-pointer hover:border-[var(--magenta)]/60 transition-colors group relative" : ""}`}
    >
      <div className="flex items-start justify-between mb-2">
        <span className="font-meta text-[10px] text-[var(--cream-soft)] tracking-[0.15em]">{label.toUpperCase()}</span>
        {Icon && <Icon className="h-4 w-4" style={{ color: accentColor, opacity: 0.7 }} />}
      </div>
      <div className="font-fraunces italic text-3xl text-[var(--cream)]" style={{ color: accent ? accentColor : undefined }}>
        {value === null || value === undefined ? "—" : (typeof value === "number" ? fmt(value) : value)}
      </div>
      {subValue && (
        <div className="font-meta text-[10px] text-[var(--cream-faint)] mt-1.5 leading-tight">{subValue}</div>
      )}
      {changePct !== null && changePct !== undefined && (
        <div className={`font-meta text-[10px] mt-1.5 inline-flex items-center gap-1 ${changePct >= 0 ? "text-green-400" : "text-red-400"}`}>
          {changePct >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
          {changePct >= 0 ? "+" : ""}{changePct}%
        </div>
      )}
      {clickable && (
        <div className="absolute bottom-2 right-3 font-meta text-[9px] tracking-[0.15em] text-[var(--cream-faint)] group-hover:text-[var(--magenta)] inline-flex items-center gap-1 opacity-60 group-hover:opacity-100">
          <Clock className="h-2.5 w-2.5" /> PR TIME
        </div>
      )}
    </Tag>
  );
}

function HourlyBreakdownModal({ metric, label, sublabel, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError("");
    api.get(`/admin/analytics/hourly`, { params: { metric, hours: 24 } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "Feil ved henting"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [metric]);

  const buckets = data?.buckets || [];
  const maxCount = Math.max(1, ...buckets.map((b) => b.count));
  const peak = buckets.reduce((acc, b) => (b.count > (acc?.count || 0) ? b : acc), null);

  // Konverter ISO hour "2026-06-13T14" → "14:00" i Oslo-tid for visning
  const hourLabel = (iso) => {
    try {
      const d = new Date(iso + ":00:00Z");
      return d.toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Oslo" });
    } catch { return iso.slice(-2) + ":00"; }
  };
  const dateLabel = (iso) => {
    try {
      const d = new Date(iso + ":00:00Z");
      return d.toLocaleDateString("nb-NO", { day: "2-digit", month: "short", timeZone: "Europe/Oslo" });
    } catch { return iso.slice(0, 10); }
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/85 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 pt-12 sm:pt-4 overflow-y-auto"
      onClick={onClose}
      data-testid="hourly-modal"
    >
      <div
        className="w-full max-w-3xl bg-[var(--noir-elev)] border border-white/10 p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[var(--cream-soft)] hover:text-[var(--cream)]"
          data-testid="hourly-modal-close"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="font-meta text-[10px] text-[var(--cream-soft)] tracking-[0.2em] uppercase mb-1">Siste 24 timer · pr. time</div>
        <h2 className="font-fraunces italic text-3xl text-[var(--cream)] mb-1">{label}</h2>
        {sublabel && (
          <div className="font-manrope text-xs text-[var(--cream-faint)] mb-3">{sublabel}</div>
        )}
        {data && (
          <div className="font-meta text-xs text-[var(--cream-soft)] mb-6">
            Totalt: <span className="text-[var(--cream)] font-bold">{data.total}</span>
            {peak && peak.count > 0 && (
              <> · Topp-time: <span className="text-[var(--magenta)]">{hourLabel(peak.hour)}</span> ({peak.count})</>
            )}
          </div>
        )}

        {loading && <div className="font-meta text-[var(--cream-soft)] py-12 text-center">Laster…</div>}
        {error && <div className="font-manrope text-sm text-red-400 py-4">{error}</div>}

        {!loading && !error && (
          <div className="space-y-1" data-testid="hourly-bars">
            {buckets.map((b, idx) => {
              const w = (b.count / maxCount) * 100;
              const isPeak = peak && b.hour === peak.hour && b.count > 0;
              // Vis dato-label på første time og hver gang dagen skifter
              const showDate = idx === 0 || (dateLabel(b.hour) !== dateLabel(buckets[idx - 1]?.hour));
              return (
                <div key={b.hour}>
                  {showDate && (
                    <div className="font-meta text-[9px] tracking-[0.2em] uppercase text-[var(--cream-faint)] pt-3 pb-1">
                      {dateLabel(b.hour)}
                    </div>
                  )}
                  <div className="flex items-center gap-3" data-testid={`hourly-row-${idx}`}>
                    <div className="font-meta text-[10px] text-[var(--cream-soft)] w-12 tabular-nums">
                      {hourLabel(b.hour)}
                    </div>
                    <div className="flex-1 h-5 bg-white/5 relative overflow-hidden">
                      <div
                        className="h-full transition-all"
                        style={{
                          width: `${w}%`,
                          background: isPeak ? "var(--magenta)" : "var(--cream-soft)",
                          opacity: b.count === 0 ? 0.2 : 1,
                        }}
                      />
                    </div>
                    <div className={`font-fraunces italic text-base w-12 text-right tabular-nums ${isPeak ? "text-[var(--magenta)]" : "text-[var(--cream)]"}`}>
                      {b.count}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-6 pt-4 border-t border-white/10 font-meta text-[10px] text-[var(--cream-faint)] tracking-[0.15em]">
          TIDSSONE: EUROPE/OSLO · BUCKETER PR. HELE TIME
        </div>
      </div>
    </div>
  );
}

function fmt(n) {
  if (n === null || n === undefined) return "—";
  return new Intl.NumberFormat("nb-NO").format(n);
}
